import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { ReservationCruise } from '../models/res-cruise.models';
import { IReservationApiAgency, ReservationAgency, ReservationIndividual } from '../models/res-owner.model';
import { IPromoParams, IResApiManagePromoProperties, IResApiSearchablePromoProperties, IReservationApiManagePromoActionParams, IReservationApiManagePromoAgenciesParams, IReservationApiManagePromoCompatibilityParams, IReservationApiManagePromoParams, IReservationApiManagePromoVoyagesParams, IReservationBkgPromoItemManagePramaters, ResBonusCommissionType, ResPromo, ResPromoActionItems, ResPromoCompany, ResPromoCompatibility, ResPromoHeader, ResPromoPackage, ResPromoPriceType, ResPromoProperty, ResPromoSuiteCategory } from '../models/res-promo.model';

@Injectable({
  providedIn: 'root'
})
export class ReservationPromoApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  public bkgPromoItemsManage(
    params: IReservationBkgPromoItemManagePramaters
  ): Observable<boolean> {
    return this.postDynamicAPI('promo-apply/manage', params).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        return (response?.errors?.length ?? 0) === 0
      })
    );
  }

  public deletePromo(params?: { PromoID: number }) {
    return this.callDynamicAPI('/promo/delete', params).pipe(
      map<DynamicApiResponse, void>(() => {
      })
    );
  }

  public getBonusCommissionType(params: { PromoID?: number }): Observable<ResBonusCommissionType[]> {
    return this.callDynamicAPI('promo/getBonusCommissionType', params).pipe(
      map<DynamicApiResponse, ResBonusCommissionType[]>((response) => {
        return response.body.map((r: any) => new ResBonusCommissionType(r))
      })
    );
  }

  public getPromoAgencies(params: { PromoID?: number }
  ): Observable<Array<ReservationAgency | ReservationIndividual>> {
    return this.callDynamicAPI('promo/getAgencies', params).pipe(
      map<DynamicApiResponse, Array<ReservationAgency | ReservationIndividual>>((response) => {
        return response.body.Agencies.map(
          (agency: IReservationApiAgency) => {
            const a = new ReservationAgency(agency);
            a.agencyTypology = "AGN";
            return a;
          }
        ).concat(response.body.Consortiums.map(
          (consortium: IReservationApiAgency) => {
            const c = new ReservationAgency(consortium);
            c.isConsortium = true;
            c.agencyTypology = "CNS";
            return c;
          }
        )).concat(response.body.HeadQuarters.map(
          (headQuarter: IReservationApiAgency) => {
            const hq = new ReservationAgency(headQuarter);
            hq.isHeadQuarter = true;
            hq.agencyTypology = "AHQ";
            return hq;
          }
        ));
      })
    );
  }

  public getPromoCompanies(params: { Promo?: string }
  ): Observable<ResPromoCompany[]> {
    return this.callDynamicAPI('promo/companies', params).pipe(
      map<DynamicApiResponse, Array<ResPromoCompany>>((response) => {
        const promoCompanies = response.body.map((r: any) => new ResPromoCompany(r));
        // const allCompanies = { companyID: 0, companyCode: '*', reportingCompanyName: 'All Companies' }
        // promoCompanies.splice(0, 0, allCompanies)
        return promoCompanies;
      })
    );
  }

  getPromoCompatibility(params: { PromoID?: number }): Observable<Array<ResPromoCompatibility>> {
    return this.callDynamicAPI('promocompatibility/get', params).pipe(
      map<DynamicApiResponse, Array<ResPromoCompatibility>>((response) => {
        return response.body.map((data: any) => new ResPromoCompatibility(data));
      })
    );
  }

  public getPromoItems(params: { PromoID?: number }
  ): Observable<ResPromoActionItems> {
    return this.callDynamicAPI('promo/getItems', params).pipe(
      map<DynamicApiResponse, ResPromoActionItems>((response) => {
        return new ResPromoActionItems(response.body)
      })
    );
  }

  getPromoPackages(promoID?: number): Observable<ResPromoPackage[]> {
    return this.getDynamicAPI('promo/getPromoPackages', { PromoID: promoID }).pipe(
      map<DynamicApiResponse, ResPromoPackage[]>((response) => {
        return response.body.map((data: any) => new ResPromoPackage(data))
      })
    );
  }

  public getPromoPaginated(params?: IPromoParams, loadOptions?: any): Promise<ResolvedData<ResPromo>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(this.postDynamicAPI('promo/search', params)).then((response: any) => {
      return {
        data: response.body.map((promo: any) => new ResPromo(promo)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  getPromoProperties(promoID: number): Observable<ResPromoProperty[]> {
    return this.getDynamicAPI('promo/getPromoProperties', { PromoID: promoID }).pipe(
      map<DynamicApiResponse, ResPromoProperty[]>((response) => {
        return response.body.map((data: any) => new ResPromoProperty(data))
      })
    );
  }

  public getPromoSuiteCategories(params: { promoId?: number }
  ): Observable<ResPromoSuiteCategory[]> {
    return this.callDynamicAPI('promo/suitecategories', params).pipe(
      map<DynamicApiResponse, Array<ResPromoSuiteCategory>>((response) => {
        const promoSuiteCategories = response.body.map((r: any) => new ResPromoSuiteCategory(r));
        const allSuiteCategories = new ResPromoSuiteCategory({ SuiteCategoryID: 0, SuiteCategory: 'ALL' })
        promoSuiteCategories.splice(0, 0, allSuiteCategories)
        return promoSuiteCategories;
      })
    );
  }

  public getPromoVoyages(params: { PromoID?: number }
  ): Observable<Array<ReservationCruise>> {
    return this.callDynamicAPI('promo/getVoyages', params).pipe(
      map<DynamicApiResponse, Array<ReservationCruise>>((response) => {
        return response.body.map((r: any) => new ReservationCruise(r))
      })
    );
  }

  public getPromos(
    params: { PromoID: number }
  ): Observable<ResPromo[]> {
    return this.callDynamicAPI('promo/get', params).pipe(
      map<DynamicApiResponse, Array<ResPromo>>((response) => {
        return response.body.map(
          (p: any) => new ResPromo(p)
        );
      })
    );
  }

  public getPromotionHeader(params: { promoID?: number }): Observable<ResPromoHeader[]> {
    return this.callDynamicAPI('promo/getPromotionHeader', params).pipe(
      map<DynamicApiResponse, Array<ResPromoHeader>>((response) => {
        const promoHeader = response.body.DT2.map((r: any) => new ResPromoHeader(r));
        return promoHeader;
      })
    );
  }

  getPromotionPriceTypes(promoID?: number, companyID?: number | string) {
    const params = companyID ? typeof companyID === 'number' ? { CompanyPromoID: companyID } : { Companies: companyID } : {};
    return this.callDynamicAPI('promoVoyages/getPriceTypes', { PromoID: promoID, ...params }).pipe(
      map<DynamicApiResponse, Array<ResPromoPriceType>>((response) =>
        response.body.map((p: any) => new ResPromoPriceType(p))
      )
    );
  }

  getSearchablePromoProperties(params: IResApiSearchablePromoProperties, loadOptions?: any): Promise<ResolvedData<ResPromoProperty>> {
    params = this.fillPaginatedParams(params, loadOptions);
    return lastValueFrom(this.callDynamicAPI('promo/getSearchableProperties', params)).then((response: any) => {
      return {
        data: response.body.map((promoProperty: any) => new ResPromoProperty(promoProperty)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  public managePromo(params: IReservationApiManagePromoParams): Observable<number> {
    return this.postDynamicAPI('promo/upsert', params).pipe(
      map<DynamicApiResponse, number>((response) => {
        return Array.isArray(response.body) && response.body.length > 0 && response.body[0].PromoID ? response.body[0].PromoID : undefined;
      })
    );
  }

  managePromoProperties(params?: IResApiManagePromoProperties) {
    return this.postDynamicAPI('promo/managePromoProperties', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public saveCompatiblePromo(params: IReservationApiManagePromoCompatibilityParams): Observable<void> {
    return this.postDynamicAPI('promocompatibility/manage', params).pipe(
      map<DynamicApiResponse, void>(() => { })
    )
  }

  public savePromoAction(params: IReservationApiManagePromoActionParams): Observable<void> {
    return this.postDynamicAPI('promo/itemsUpsert', params).pipe(
      map<DynamicApiResponse, void>(() => {
      })
    );
  }

  public savePromoAgencies(params: IReservationApiManagePromoAgenciesParams): Observable<void> {
    return this.postDynamicAPI('promo/agenciesUpsert', params).pipe(
      map<DynamicApiResponse, void>(() => {
      })
    )
  }

  public savePromoVoyages(params: IReservationApiManagePromoVoyagesParams): Observable<void> {
    return this.postDynamicAPI('/promo/voyagesUpsert', params).pipe(
      map<DynamicApiResponse, void>(() => { })
    )
  }
}
